import React from 'react';
import { Button } from './ui/button';
import { ArrowRight, CheckCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useSiteSettings } from '../context/SiteSettingsContext';
import { resolveImageUrl } from '../utils/resolveImageUrl';

const Financing = () => {
  const { settings } = useSiteSettings();
  const benefits = [
    "Get pre-approved in minutes",
    "Competitive interest rates",
    "Flexible payment terms",
    "No hidden fees guaranteed"
  ];

  return (
    <section className="py-20 lg:py-24 bg-black relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-red-600 rounded-full opacity-10 blur-3xl"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-red-600 rounded-full opacity-10 blur-3xl"></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left content */}
          <div>
            <div className="flex items-center justify-start gap-3 mb-4">
              <span className="h-px w-8 bg-red-600"></span>
              <span className="text-red-500 font-semibold text-xs uppercase tracking-[0.22em]" data-testid="homepage-apply-now-tag">Apply Now</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-display font-bold text-white tracking-tight mb-6" data-testid="homepage-apply-now-heading">
              Apply for Your Next Vehicle
            </h2>
            <p className="text-gray-300 text-lg mb-8 leading-relaxed">
              Complete your application in minutes and let our team help you find the right approval path for your budget.
            </p>

            {/* Benefits list */}
            <ul className="space-y-4 mb-8">
              {benefits.map((benefit, index) => (
                <li key={index} className="flex items-center gap-3 text-white">
                  <div className="w-6 h-6 bg-red-600 rounded-full flex items-center justify-center flex-shrink-0">
                    <CheckCircle size={14} className="text-white" />
                  </div>
                  <span>{benefit}</span>
                </li>
              ))}
            </ul>

            <Link to="/apply-now">
              <Button className="bg-red-600 hover:bg-red-700 text-white rounded-full px-7 py-3.5 text-lg font-semibold transition-all duration-300 hover:shadow-xl hover:shadow-red-600/30 flex items-center gap-2 group" data-testid="homepage-apply-now-button">
                Apply Now
                <ArrowRight size={20} className="transition-transform group-hover:translate-x-1" />
              </Button>
            </Link>
          </div>

          {/* Right image */}
          <div className="relative">
            <img
              src={resolveImageUrl(settings.financeImage) || "/logo.jpg"}
              alt="Finance your vehicle"
              className={`w-full h-[340px] sm:h-[400px] rounded-2xl ${settings.financeImage ? 'object-cover' : 'object-contain bg-white p-6'}`}
            />
            {/* Overlay card — tucked under the image on phones so it can't run off-screen */}
            <div className="relative -mt-8 mx-4 sm:mx-0 sm:mt-0 sm:absolute sm:-bottom-6 sm:-right-6 bg-white p-6 rounded-2xl ring-1 ring-gray-200 shadow-2xl sm:max-w-xs">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                  <CheckCircle size={24} className="text-green-600" />
                </div>
                <div>
                  <p className="font-bold text-gray-900">Quick Approval</p>
                  <p className="text-sm text-gray-500">Results in minutes</p>
                </div>
              </div>
              <p className="text-gray-600 text-sm">
                Most applications approved within 24 hours
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Financing;
